import React, { useState, useEffect, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { recaptchaService } from '../../services/googleServices';

const GoogleReCAPTCHA = ({ 
  onVerify, 
  onError, 
  siteKey = null, 
  theme = 'light',
  size = 'normal',
  className = '',
  disabled = false,
  page = null // New prop to specify which page this is for
}) => {
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const recaptchaRef = useRef(null);

  // Load reCAPTCHA configuration
  useEffect(() => {
    const loadConfig = async () => {
      try {
        let response;
        if (page) {
          // Get page-specific settings
          response = await recaptchaService.getPageConfig(page);
        } else {
          // Get general config
          response = await recaptchaService.getConfig();
        }
        
        if (response.success) {
          setConfig(response.data);
        }
      } catch (error) {
        console.error('Failed to load reCAPTCHA config:', error);
      }
    };

    loadConfig();
  }, [page]);

  // Handle reCAPTCHA change
  const handleChange = async (token) => {
    if (!token) {
      setIsVerified(false);
      onVerify && onVerify(null);
      return;
    }

    setIsLoading(true);
    try {
      const result = await recaptchaService.verifyToken(token);
      
      if (result.success && result.data.verified) {
        setIsVerified(true);
        onVerify && onVerify(token);
      } else {
        setIsVerified(false);
        onError && onError(result.data.errorCodes || ['Verification failed']);
      }
    } catch (error) {
      console.error('reCAPTCHA verification error:', error);
      setIsVerified(false);
      onError && onError(['Verification failed']);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset reCAPTCHA
  const reset = () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
    setIsVerified(false);
  };

  // If reCAPTCHA is disabled for this page, call onVerify with disabled token
  useEffect(() => {
    if (config && config.enabled === false) {
      onVerify && onVerify('DISABLED');
    }
  }, [config, onVerify]);

  // If reCAPTCHA is disabled for this page, don't render
  if (config && config.enabled === false) {
    return null;
  }

  // If no site key is provided and config is not loaded, show loading
  if (!siteKey && !config?.siteKey) {
    return (
      <div className={`flex items-center justify-center p-4 ${className}`}>
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-sm text-gray-600">Loading reCAPTCHA...</span>
      </div>
    );
  }

  const finalSiteKey = siteKey || config?.siteKey;

  return (
    <div className={`${className}`}>
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={finalSiteKey}
        onChange={handleChange}
        theme={theme}
        size={size}
        disabled={disabled}
        className="transform scale-90 origin-left"
      />
      
      {isLoading && (
        <div className="mt-2 text-sm text-blue-600">
          Verifying...
        </div>
      )}
      
      {isVerified && (
        <div className="mt-2 text-sm text-green-600 flex items-center">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          Verified
        </div>
      )}
    </div>
  );
};

export default GoogleReCAPTCHA;
